import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus";
import { BloodTestTracker } from "../api-objects/BloodTestTracker";
import { DnaTestTracker } from "../api-objects/DnaTestTracker";
import { BASE_URL, requestOptionsGET } from "../Constants";
import { BloodTestTrackingStatus } from "../enums/TrackingStatus";
import { ErrorWithMessageCallback, SuccessWithMessageCallback, SuccessWithTrackerCallback, UpdateTrackerByOrderIdSuccessCallback } from "./callback-helpers";

/**
 * Fetches the DNA Test Tracker data for a given user.
 * 
 * This function sends a request to the API to retrieve tracking information for a specific user.
 * If the user has no tracking data, it returns `null`. It also handles authentication errors and
 * general request failures.
 * 
 * @param {string} userId - The unique identifier of the user.
 * @param {string} idToken - The authentication token for API authorization.
 * @returns {Promise<DnaTestTracker[] | null>} - A promise that resolves to an array of `DnaTestTracker` objects
 *                                              if tracking data exists, or `null` if no data is found.
 * @throws {Error} - Throws an error if the request fails due to network issues, authentication failure,
 *                   or other unexpected API errors.
 * 
 * @example
 * try {
 *   const trackers = await getTrackerByUserId("12345", "your-id-token");
 *   if (trackers) {
 *      console.log("Tracking data:", trackers);
 *   } else {
 *      console.log("No tracking data found.");
 *   }
 * } catch (error) {
 *   console.error("Failed to fetch tracking data:", error.message);
 * }
 */
export async function getTrackerByUserId(
    userId: string,
    idToken: string,
): Promise<DnaTestTracker[] | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/track/${userId}`, requestOptionsGET(idToken));
        if (response.ok) {
            const trackers: DnaTestTracker[] = await response.json();
            return trackers;
        } else if (response.status === 401) {
            throw new Error("Session expired");
        } else if (response.status === 404) {
            console.warn(`Tracker non trovato per user: ${userId}`)
            return null;
        } else {
            throw new Error(response.statusText || "Failed to fetch trackers");
        }
    } catch (error) {
        console.error("Get tracker failed:", error);
        throw error instanceof Error ? error : new Error("An unexpected error occurred");
    }
}

export async function updateTrackerByOrderId(
    orderId: string,
    idToken: string,
    status: DnaTestTrackingStatus,
    onSuccess: UpdateTrackerByOrderIdSuccessCallback,
    onError: ErrorWithMessageCallback,
    handleSessionExpiry?: () => void,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/track/order/${orderId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify(
                {
                    tracking_status: status,
                }
            )
        })
        if (response.ok) {
            const tracker: DnaTestTracker = await response.json()
            onSuccess(tracker)
        } else if (response.status === 401 && handleSessionExpiry) {
            handleSessionExpiry();
        } else {
            const errorMessage = response.statusText || "Failure";
            onError(errorMessage);
        }
    } catch (error) {
        console.error("Update tracker failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function createBloodTestTrackItem(
    user_id: string,
    id_token: string,
    order_id: string,
    tracking_status: BloodTestTrackingStatus,
    onSuccess: SuccessWithMessageCallback,
    onError: ErrorWithMessageCallback,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/blood-track`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': id_token,
            },
            body: JSON.stringify({
                user_id,
                order_id,
                tracking_status,
            }),
        })
        if (response.ok) {
            const message: string = await response.json()
            onSuccess(message)
        } else {
            onError("errore")
        }
    } catch (error) {
        console.error("Create blood test tracker failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}

export async function getBloodTrackerByUserId(
    userId: string,
    idToken: string,
): Promise<BloodTestTracker[] | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/blood-track/${userId}`, requestOptionsGET(idToken));
        if (response.ok) {
            const tracker: BloodTestTracker[] = await response.json();
            return tracker;
        } else if (response.status === 401) {
            throw new Error("Session expired");
        } else if (response.status === 404) {
            console.warn(`Blood tracker non trovato per user: ${userId}`)
            return null;
        } else {
            const errorMessage = response.statusText || "Failed to fetch blood tracker";
            throw new Error(errorMessage);
        }
    } catch (error) {
        console.error("Get blood tracker failed:", error);
        if (error instanceof Error) {
            throw error;
        } else {
            throw new Error("An unexpected error occurred");
        }
    }
}


export async function updateBloodTrackerByUserId(
    userId: string,
    idToken: string,
    status: BloodTestTrackingStatus,
    onSuccess: SuccessWithTrackerCallback,
    onError: ErrorWithMessageCallback,
) {
    try {
        const response = await fetch(`${BASE_URL}/api/blood-track/user_id/${userId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify(
                {
                    tracking_status: status,
                }
            )
        })
        if (response.ok) {
            const tracker: BloodTestTracker = await response.json()
            onSuccess(tracker)
        } else {
            const errorMessage = response.statusText || "Failure";
            onError(errorMessage);
        }
    } catch (error) {
        console.error("Get blood tracker failed:", error);
        return { success: false, error: "An unexpected error occurred" };
    }
}